import { Signal } from '@angular/core';
import { SmsGateway } from '@tourfold/libraries/tf-api-client-public-v2';
import { providePageContext } from '@tourfold/shared-kernels/page-context';

const MAX_VISIBLE_GATEWAYS = 25;

export interface SmsGatewayAiPageContextSignals {
  readonly configurations: Signal<SmsGateway[]>;
  readonly editingConfiguration: Signal<SmsGateway | null>;
  readonly testingConfiguration: Signal<SmsGateway | null>;
  readonly activeTab: Signal<string>;
}

/**
 * Registers AI assistant page context for /settings/sms-gateway.
 *
 * - `entity` = the gateway being edited (dialog open) wins; otherwise the
 *   gateway being tested; otherwise undefined.
 * - `visible_rows` = first 25 configured gateways by name.
 * - `filters.tab` = active tab (`configuration` | `report`) so the
 *   assistant knows whether the user is on the gateway list or the
 *   delivery-report log.
 *
 * See frontend/AGENTS.md → "AI Assistant Page Context" for the field rules.
 */
export function provideSmsGatewayAiPageContext(state: SmsGatewayAiPageContextSignals): void {
  providePageContext(() => {
    const focused = state.editingConfiguration() ?? state.testingConfiguration();

    return {
      entity: focused
        ? {
            type: 'sms_gateway',
            id: focused.id,
            label: focused.name,
          }
        : undefined,
      visible_rows: state
        .configurations()
        .slice(0, MAX_VISIBLE_GATEWAYS)
        .map((gateway) => ({
          type: 'sms_gateway',
          id: gateway.id,
          label: gateway.name,
        })),
      filters: {
        tab: state.activeTab(),
      },
    };
  });
}
